/**
 * The full analysis, in its parts (References / Detail · Analysis in parts).
 *
 * The analyzers write markdown under numbered `## N. Heading` sections. A video
 * shot report has eight; the Pen shows four tabs for it, Overview, Scenes,
 * Speech and Themes. Overview holds only the overview section; the remaining
 * sections (production, highlights, takeaways, flags) are not shown. Any other analysis (an image read, an article) gets one tab per
 * section, and one with no headings at all is a single Overview.
 */

export interface AnalysisScene {
  /** Seconds from the start, and where the scene ends (null when unstated). */
  start: number;
  end: number | null;
  stamp: string;
  text: string;
}

export interface AnalysisPart {
  key: string;
  label: string;
  /** Shown beside the tab label: scenes and themes are counted. */
  count: number | null;
  /** Markdown body; empty means the part says nothing ("No spoken content"). */
  markdown: string;
  scenes?: AnalysisScene[];
  /** The analyzer's own words for an empty part, shown as one quiet line. */
  emptyLine?: string;
}

export interface ParsedAnalysis {
  kind: "shot report" | "analysis";
  parts: AnalysisPart[];
}

interface Section {
  title: string;
  body: string;
}

const EMPTY = /^(no spoken content|none detected|not applicable|none|n\/a)\.?$/i;

function sections(markdown: string): Section[] {
  const out: Section[] = [];
  let current: Section | null = null;
  for (const line of markdown.split(/\r?\n/)) {
    const heading = /^#{2,3}\s+(?:\d+\.\s*)?(.+?)\s*$/.exec(line);
    if (heading) {
      if (current) out.push(current);
      current = { title: heading[1], body: "" };
    } else if (current) {
      current.body += `${line}\n`;
    } else if (line.trim()) {
      current = { title: "Overview", body: `${line}\n` };
    }
  }
  if (current) out.push(current);
  return out.map((s) => ({ ...s, body: s.body.trim() }));
}

/** "00:01", "1:02:03" → seconds. */
function seconds(stamp: string): number {
  return stamp.split(":").reduce((total, part) => total * 60 + Number(part), 0);
}

/** "00:07" → "0:07", the Pen's stamp. */
function shortStamp(stamp: string): string {
  const total = seconds(stamp);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/** A timestamped line per entry. Scenes are bullets; a transcript's lines
 *  ("[00:05] Speaker 1: ...") may or may not be, so the bullet is optional there. */
function parseScenes(body: string, bulletOptional = false): AnalysisScene[] {
  const scenes: AnalysisScene[] = [];
  const lead = bulletOptional ? "(?:[-*]\\s*)?" : "[-*]\\s*";
  const pattern = new RegExp(
    `^\\s*${lead}\\**\\[?(\\d{1,2}(?::\\d{2}){1,2})(?:\\s*[–-]\\s*(\\d{1,2}(?::\\d{2}){1,2}))?\\]?\\**\\s*[—–:-]*\\s*(.+)$`,
  );
  for (const line of body.split("\n")) {
    const match = pattern.exec(line);
    if (!match) continue;
    scenes.push({
      start: seconds(match[1]),
      end: match[2] ? seconds(match[2]) : null,
      stamp: shortStamp(match[1]),
      text: match[3].replace(/\*\*/g, "").trim(),
    });
  }
  return scenes;
}

const bulletCount = (body: string) => body.split("\n").filter((l) => /^\s*[-*]\s+/.test(l)).length;

function part(key: string, label: string, body: string, count: number | null = null): AnalysisPart {
  const empty = EMPTY.test(body.trim());
  return {
    key,
    label,
    count,
    markdown: empty ? "" : body,
    emptyLine: empty ? body.trim() : !body.trim() ? "Nothing in this part." : undefined,
  };
}

export function parseAnalysis(markdown: string | null | undefined): ParsedAnalysis | null {
  if (!markdown?.trim()) return null;
  const all = sections(markdown);
  const find = (re: RegExp) => all.find((s) => re.test(s.title));

  const scenes = find(/scene/i);
  if (scenes) {
    const overview = find(/overview|summary/i);
    const speech = find(/transcri|speech/i);
    const themes = find(/topic|theme/i);
    // Overview shows the overview section only. The analyzer's other sections
    // (production, highlights, takeaways, flags) are not shown for now.
    const overviewBody = overview?.body ?? "";
    const sceneList = parseScenes(scenes.body);
    const parts: AnalysisPart[] = [
      part("overview", "Overview", overviewBody),
      {
        ...part("scenes", "Scenes", sceneList.length ? "" : scenes.body, sceneList.length || null),
        scenes: sceneList,
        emptyLine: sceneList.length ? undefined : part("scenes", "Scenes", scenes.body).emptyLine,
        markdown: sceneList.length ? "" : scenes.body,
      },
    ];
    if (speech) {
      // Timestamped lines read like scenes (a time, then the words); a
      // transcript with no timestamps stays as plain text.
      const lines = parseScenes(speech.body, true);
      const base = part("speech", "Speech", lines.length ? "" : speech.body, lines.length || null);
      parts.push(lines.length ? { ...base, scenes: lines, emptyLine: undefined } : base);
    }
    if (themes) parts.push(part("themes", "Themes", themes.body, bulletCount(themes.body) || null));
    return { kind: "shot report", parts };
  }

  if (all.length <= 1) {
    return { kind: "analysis", parts: [part("overview", "Overview", all[0]?.body ?? markdown.trim())] };
  }
  return {
    kind: "analysis",
    parts: all.map((s, i) => part(`part-${i}`, s.title, s.body)),
  };
}
